import { ReactElement, ReactNode, useState, useMemo, Children, isValidElement } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { cn } from '@/shared/lib/cn'
import { BentoItem } from '@/shared/components/BentoGrid'

interface SortableBentoGridProps {
  children: ReactNode
  className?: string
  items: string[]
  onReorder: (items: string[]) => void
}

export function SortableBentoGrid({
  children,
  className,
  items,
  onReorder
}: SortableBentoGridProps) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string)
      const newIndex = items.indexOf(over.id as string)
      const newItems = arrayMove(items, oldIndex, newIndex)
      onReorder(newItems)
    }

    setActiveId(null)
  }

  const activeItem = useMemo(() => {
    if (!activeId) return null
    const childArray = Children.toArray(children)
    return childArray.find((child) =>
      isValidElement(child) && child.props.id === activeId
    ) as ReactElement | undefined
  }, [activeId, children])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div
          className={cn(
            'grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 max-w-[1600px] mx-auto',
            className
          )}
        >
          {children}
        </div>
      </SortableContext>

      <DragOverlay adjustScale={true}>
        {activeItem ? (
          <BentoItem
            colSpan={activeItem.props.colSpan}
            rowSpan={activeItem.props.rowSpan}
            className={cn(
              "cursor-grabbing shadow-2xl scale-[1.02] z-50",
              // Ensure background is opaque and styled nicely for the overlay
              "bg-[#1C1C1C] border border-white/10 rounded-xl overflow-hidden"
            )}
          >
            {activeItem.props.children}
          </BentoItem>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
